import * as React from "react";
import { CloudDownload, Building2Icon, DatabaseIcon, FileTextIcon, FolderIcon, LayoutGridIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import axios from "axios";
import { clsx } from "clsx";
import TopBarProgress from "react-topbar-progress-indicator";

import { UserAvatar } from "@/components/ui/userAvatar";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { SharepointItem } from "@/modules/sharepoint/types";
import { SHAREPOINT_DAILY_SYNC_LIMIT, useSharepointSync } from "@/modules/sharepoint/hooks/useSharepointSync";
import { useLazyGetFileDetailsQuery } from "@/api/files";
import * as analytics from "@/services/analytics";
import { toast } from "react-toastify";

interface Props {
  item: SharepointItem;
}

export const SharepointRow: React.FC<Props> = ({ item }) => {
  const [searchParams] = useSearchParams();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const navigate = useNavigate();
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [refetchSharepointFolder] = useLazyGetFileDetailsQuery();
  const { recentItems, addRecentItem, canSync } = useSharepointSync();
  const { formatDate } = useDateFormatter();

  const handleDownload = async () => {
    if (!canSync) {
      toast.error(`You can only sync ${SHAREPOINT_DAILY_SYNC_LIMIT} files per day`);
      return;
    }

    setIsDownloading(true);
    setIsSyncing(true);
    analytics.sharepointSync(item.name);

    try {
      const response = await axios.post<{ parent_fse_id: string; tx_id: string }>("/pf-mainweb/document/sharepoint", {
        download_url: item.download_url,
        filename: item.name,
      });

      addRecentItem(item.id);
      refetchSharepointFolder({ fse_id: response.data.parent_fse_id });
      toast.success("Download started, file will be add to My folder");
      navigate(`/drive/${response.data.parent_fse_id}?tx_id=${response.data.tx_id}`);
    } catch (error) {
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
      toast.error(typeof detail === "string" ? detail : "Could not sync this file");
    } finally {
      setIsDownloading(false);
      setIsSyncing(false);
    }
  };

  const handleClick = () => {
    const currentMode = searchParams.get("mode") || "menu";
    const currentSiteId = searchParams.get("site_id");
    const currentDriveId = searchParams.get("drive_id");

    switch (item.type) {
      case "menu_option":
        // Navigate to selected mode (onedrive or sharepoint)
        navigate(`/sharepoint?mode=${item.id}`);
        break;

      case "site":
        // Navigate to site's document libraries
        navigate(`/sharepoint?mode=sharepoint&site_id=${item.id}`);
        break;

      case "drive":
        // Navigate to drive's root folder
        navigate(`/sharepoint?mode=sharepoint&site_id=${currentSiteId}&drive_id=${item.id}`);
        break;

      case "folder":
        // Navigate into folder
        if (currentMode === "onedrive") {
          navigate(`/sharepoint?mode=onedrive&folder_id=${item.id}`);
        } else if (currentMode === "sharepoint") {
          navigate(
            `/sharepoint?mode=sharepoint&site_id=${currentSiteId}&drive_id=${currentDriveId}&folder_id=${item.id}`,
          );
        }
        break;

      default:
        break;
    }
  };

  const isSynced = recentItems.some(i => i.id === item.id);

  // Determine if item is clickable
  const isClickable = item.actions?.can_navigate || item.type === "folder";

  const iconMap = {
    menu_option: <LayoutGridIcon className="mr-2 h-5 w-5 text-gray-500" />,
    site: <Building2Icon className="mr-2 h-5 w-5 text-blue-600" />,
    drive: <DatabaseIcon className="mr-2 h-5 w-5 text-purple-500" />,
    folder: <FolderIcon className="mr-2 h-5 w-5 text-yellow-500" />,
    file: <FileTextIcon className="mr-2 h-5 w-5 text-blue-500" />,
  };

  const getIcon = () => iconMap[item.type] || <FileTextIcon className="mr-2 h-5 w-5 text-gray-500" />;

  return (
    <>
      {isSyncing && <TopBarProgress />}
      <div className="relative flex flex-col items-start gap-4 p-4 hover:bg-ivory-mist-400 md:flex-row md:items-center md:p-6">
        <div className="relative w-full md:w-1/2 lg:w-7/12">
          <div
            className={clsx("flex items-center", {
              "cursor-pointer": isClickable,
            })}
            onClick={isClickable ? handleClick : undefined}
          >
            {getIcon()}
            <span className="flex truncate font-medium">{item.name}</span>
          </div>
          {(item.created_at || item.owner) && (
            <div className="mt-1 flex flex-wrap items-center text-xs text-gray-500">
              {item.created_at && <span className="mr-2">{formatDate(item.created_at)}</span>}
              {item.owner && (
                <>
                  <UserAvatar user={{ name: item.owner }} size="xs" />
                  <span className="ml-2 truncate">{item.owner}</span>
                </>
              )}
            </div>
          )}
        </div>
        {item.type === "file" && item.actions?.can_download && (
          <Button
            disabled={isDownloading || isSynced}
            variant="secondary"
            size="sm"
            className="ml-auto w-full text-xs sm:w-auto"
            onClick={handleDownload}
          >
            <CloudDownload className="mr-1 h-4 w-4" />
            {isSynced ? "Synced" : "Sync"}
          </Button>
        )}
      </div>
    </>
  );
};
